"use client";

import type { PropsWithChildren, ReactNode } from "react";

import { LinkIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import { Sheet } from "react-modal-sheet";

import { robotoCondensed } from "@/fonts";

type ModalContentProps = PropsWithChildren<{
  readonly aside?: ReactNode;
  readonly description: string;
  readonly title: string;
  readonly url?: null | string;
}>;

export function ModalContent({
  aside = null,
  children,
  description,
  title,
  url,
}: ModalContentProps): ReactNode {
  return (
    <article
      className={clsx(
        "grid h-full grid-cols-1 grid-rows-[min-content_2fr_min-content] pb-4",
        "md:m-auto md:w-[1024px] md:grid-cols-[25%_75%] md:grid-rows-[min-content_1fr_1fr_1fr_1fr] md:gap-x-8 md:pb-0",
      )}
    >
      <header
        className={clsx("mb-5 px-4", {
          "md:border-gray-light md:border-b md:pb-4": aside !== null,
        })}
      >
        <h2
          className={clsx(
            "font-bold-antialiased mb-2 text-center text-2xl",
            robotoCondensed.className,
          )}
        >
          {url ? (
            <a
              className={clsx(
                "flex items-center justify-center gap-1 underline underline-offset-4",
                "hover:text-primary",
              )}
              href={url}
            >
              {title}
              <LinkIcon height="24px" width="24px" />
            </a>
          ) : (
            title
          )}
        </h2>
        <div className="text-center text-sm">{description}</div>
      </header>
      <Sheet.Scroller
        className={clsx("h-full overflow-auto px-4", "md:row-span-5")}
      >
        <div
          className={clsx(
            "text-text relative flex flex-col items-stretch gap-4 overflow-auto pb-4",
            "md:pr-8",
          )}
        >
          {children}
        </div>
      </Sheet.Scroller>
      {aside ? (
        <aside
          className={clsx(
            "border-t-gray-light border-t px-4 pt-4",
            "md:border-t-0 md:pt-0",
          )}
        >
          {aside}
        </aside>
      ) : null}
    </article>
  );
}
